import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { PolicyWorkspacePage } from '@/components/layout/PolicyWorkspacePage';
import { RequireProviderSession } from '@/components/layout/RequireProviderSession';
import { NotAvailableYet } from '@/components/shared/NotAvailableYet';
import { LoginScreen } from '@/components/layout/LoginScreen';
import { ContactPage } from '@/features/contact';
import { FaqPage } from '@/features/faq';
import { FormsPage } from '@/features/forms';
import { PrivacyPolicyPage, TermsPage } from '@/features/legal';

/**
 * The single route → page map. Features are added here and nowhere else; a page component never
 * registers its own route.
 *
 * Contact and FAQ are PUBLIC; Forms is not.
 *
 * Their stories originally said otherwise — US-027 AC-003 and US-019 AC-005 each asked for an
 * unauthenticated visitor to be redirected to login, which collided with US-001 AC-005 asking for
 * Contact and FAQ links on the login screen. The links were rendered as specified and every one of them
 * bounced the visitor straight back. The BA resolved it in favour of the links (Zach Jamani, defect on
 * the login screen): an unauthenticated visitor may read both pages, so US-027 AC-003 and US-019 AC-005
 * are superseded and need amending.
 *
 * US-017 AC-004 was not part of that call, so Forms stays behind the guard. Neither page shows provider
 * data — they are static contact details and FAQ copy — which is what makes opening them safe.
 */
const routes: RouteObject[] = [
  { path: '/login', element: <LoginScreen /> },
  {
    element: <AppShell />,
    children: [
      { path: '/contact', element: <ContactPage /> },
      { path: '/faq', element: <FaqPage /> },
      // PP-029: public, and inside the shell rather than replacing it, so opening either one from the
      // login screen or mid-session never disturbs the session behind it (AC-001, AC-003).
      { path: '/privacy-policy', element: <PrivacyPolicyPage /> },
      { path: '/terms', element: <TermsPage /> },
      {
        element: <RequireProviderSession />,
        children: [
          { path: '/', element: <PolicyWorkspacePage /> },
          { path: '/forms', element: <FormsPage /> },
          // SCAFFOLD: Claims detail routes land here as they arrive.
          //
          // The internal AI Chat surface (US-020…US-025) does NOT belong in this table. portal-spa is
          // served publicly through APIM at /*, ai-chat-svc may never have a public path (SAD-001), and
          // RequireProviderSession is navigation rather than authorization — a route here would put a
          // CSR-only surface on the internet behind a client-side check.
        ],
      },
      // SCAFFOLD: a real 404 page is still to be designed; until then an unknown URL lands here.
      { path: '*', element: <NotAvailableYet /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
